import React, { useState } from 'react';

export interface IChartSeries {
    key: string;
    label: string;
    color?: string;
}

export interface IChartData {
    type?: 'line' | 'bar' | 'column' | 'area' | 'pie' | string;
    title?: string;
    xKey?: string;
    series?: IChartSeries[];
    unit?: string;
    data?: Array<Record<string, any>>;
}

interface ChartRendererProps {
    chartData: IChartData;
    isDark?: boolean;
}

const DEFAULT_COLORS = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2'];

export const ChartRenderer: React.FC<ChartRendererProps> = ({ chartData, isDark }) => {
    const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

    const { title, xKey = 'periodo', series = [], unit = '', data = [] } = chartData;

    if (!data || data.length === 0) {
        return null;
    }

    // Default primary series if none provided
    const chartSeries: IChartSeries[] =
        series.length > 0
            ? series
            : [
                  {
                      key: Object.keys(data[0]).find((k) => k !== xKey && typeof data[0][k] === 'number') || 'valor',
                      label: 'Valor',
                      color: '#1890ff',
                  },
              ];

    const formatValue = (val: number | string) => {
        const num = typeof val === 'number' ? val : Number(val);
        if (isNaN(num)) return String(val);

        const formatted = num.toLocaleString('pt-BR', {
            maximumFractionDigits: 2,
        });

        if (unit === 'R$' || unit.toLowerCase().includes('r$')) {
            return `R$ ${formatted}`;
        }
        if (unit) {
            return `${formatted} ${unit}`;
        }
        return formatted;
    };

    // Calculate chart boundaries
    const width = 640;
    const height = 240;
    const padLeft = 75;
    const padRight = 30;
    const padTop = 25;
    const padBottom = 40;

    const chartW = width - padLeft - padRight;
    const chartH = height - padTop - padBottom;

    // Collect all numeric values across series
    let allValues: number[] = [];
    data.forEach((d) => {
        chartSeries.forEach((s) => {
            const v = Number(d[s.key]);
            if (!isNaN(v)) allValues.push(v);
        });
    });

    if (allValues.length === 0) allValues = [0];

    const minVal = Math.min(0, ...allValues);
    const maxVal = Math.max(...allValues, 1);
    const valRange = maxVal - minVal || 1;

    const getY = (val: number) => {
        return padTop + chartH - ((val - minVal) / valRange) * chartH;
    };

    // Y Axis ticks (4 ticks)
    const yTicks = [0, 0.33, 0.66, 1].map((ratio) => {
        const v = minVal + ratio * valRange;
        return {
            val: v,
            y: padTop + chartH - ratio * chartH,
        };
    });

    const primaryColor = chartSeries[0]?.color || DEFAULT_COLORS[0];

    return (
        <div
            style={{
                margin: '14px 0',
                background: isDark ? '#141414' : '#fafafa',
                border: `1px solid ${isDark ? '#303030' : '#f0f0f0'}`,
                borderRadius: '8px',
                padding: '14px 16px',
            }}
        >
            {title && (
                <div
                    style={{
                        fontWeight: 600,
                        fontSize: '13.5px',
                        marginBottom: '10px',
                        color: isDark ? '#f0f0f0' : '#262626',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <span>{title}</span>
                    {chartSeries.length > 0 && (
                        <div style={{ display: 'flex', gap: '12px', fontSize: '11.5px' }}>
                            {chartSeries.map((s, idx) => (
                                <span
                                    key={s.key}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        color: isDark ? '#bfbfbf' : '#595959',
                                    }}
                                >
                                    <span
                                        style={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '2px',
                                            background: s.color || DEFAULT_COLORS[idx % DEFAULT_COLORS.length],
                                            display: 'inline-block',
                                        }}
                                    />
                                    {s.label}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <div style={{ width: '100%', overflowX: 'auto' }}>
                <svg
                    viewBox={`0 0 ${width} ${height}`}
                    style={{ width: '100%', height: 'auto', minWidth: '460px', display: 'block' }}
                >
                    {/* Horizontal Grid lines and Y axis labels */}
                    {yTicks.map((tick, idx) => (
                        <g key={idx}>
                            <line
                                x1={padLeft}
                                y1={tick.y}
                                x2={padLeft + chartW}
                                y2={tick.y}
                                stroke={isDark ? '#262626' : '#f0f0f0'}
                                strokeDasharray="3 3"
                            />
                            <text
                                x={padLeft - 8}
                                y={tick.y + 4}
                                textAnchor="end"
                                fontSize="10.5"
                                fill={isDark ? '#8c8c8c' : '#8c8c8c'}
                                fontFamily="sans-serif"
                            >
                                {formatValue(tick.val)}
                            </text>
                        </g>
                    ))}

                    {/* Gráfico de Barras / Colunas */}
                    {data.map((d, dIdx) => {
                        const totalSeries = chartSeries.length;
                        const slotWidth = chartW / data.length;
                        const barWidth = Math.max(16, Math.min(36, (slotWidth - 12) / totalSeries));
                        const slotStartX = padLeft + dIdx * slotWidth + (slotWidth - barWidth * totalSeries) / 2;
                        const isHov = hoveredIdx === dIdx;

                        return (
                            <g key={dIdx}>
                                {chartSeries.map((s, sIdx) => {
                                    const v = Number(d[s.key]) || 0;
                                    const barX = slotStartX + sIdx * barWidth;
                                    const barY = getY(v);
                                    const barH = padTop + chartH - barY;
                                    const color = s.color || DEFAULT_COLORS[sIdx % DEFAULT_COLORS.length];

                                    return (
                                        <g key={s.key}>
                                            <rect
                                                x={barX}
                                                y={barY}
                                                width={barWidth - 2}
                                                height={Math.max(0, barH)}
                                                rx="4"
                                                fill={color}
                                                opacity={isHov ? 1 : 0.85}
                                                style={{ transition: 'all 0.2s ease', cursor: 'pointer' }}
                                                onMouseEnter={() => setHoveredIdx(dIdx)}
                                                onMouseLeave={() => setHoveredIdx(null)}
                                            />
                                        </g>
                                    );
                                })}
                            </g>
                        );
                    })}

                    {/* X Axis Labels */}
                    {data.map((d, dIdx) => {
                        const slotWidth = chartW / data.length;
                        const x = padLeft + dIdx * slotWidth + slotWidth / 2;
                        const label = String(d[xKey] ?? d.periodo ?? d.ano ?? d.mes ?? d.name ?? dIdx + 1);

                        return (
                            <text
                                key={dIdx}
                                x={x}
                                y={padTop + chartH + 18}
                                textAnchor="middle"
                                fontSize="11"
                                fontWeight={hoveredIdx === dIdx ? 600 : 400}
                                fill={
                                    hoveredIdx === dIdx
                                        ? primaryColor
                                        : isDark
                                        ? '#8c8c8c'
                                        : '#595959'
                                }
                                fontFamily="sans-serif"
                            >
                                {label}
                            </text>
                        );
                    })}

                    {/* Hover Tooltip Overlay */}
                    {hoveredIdx !== null && data[hoveredIdx] && (
                        <g>
                            {(() => {
                                const d = data[hoveredIdx];
                                const slotWidth = chartW / data.length;
                                const x = padLeft + hoveredIdx * slotWidth + slotWidth / 2;
                                const firstSeriesKey = chartSeries[0].key;
                                const v = d[firstSeriesKey];
                                const tooltipText = `${d[xKey] || ''}: ${formatValue(v)}`;
                                const tipW = Math.max(110, tooltipText.length * 7.5 + 16);
                                const tipX = Math.min(Math.max(x - tipW / 2, 10), width - tipW - 10);
                                const tipY = Math.max(getY(Number(v) || 0) - 34, 5);

                                return (
                                    <>
                                        <rect
                                            x={tipX}
                                            y={tipY}
                                            width={tipW}
                                            height="24"
                                            rx="4"
                                            fill={isDark ? '#262626' : '#1f1f1f'}
                                            opacity="0.94"
                                        />
                                        <text
                                            x={tipX + tipW / 2}
                                            y={tipY + 16}
                                            textAnchor="middle"
                                            fontSize="11.5"
                                            fontWeight="500"
                                            fill="#fff"
                                            fontFamily="sans-serif"
                                        >
                                            {tooltipText}
                                        </text>
                                    </>
                                );
                            })()}
                        </g>
                    )}
                </svg>
            </div>
        </div>
    );
};

export default ChartRenderer;
